"use client";

import { useState } from "react";
import { Auth_Input_Class, Auth_Label_Class } from "@/components/auth/auth-styles";
import {
  Password_Strength_Bar_Colors,
  Password_Strength_Score,
} from "@/lib/auth/password-strength";

type Password_Field_Props = {
  Id: string;
  Label: string;
  Value: string;
  On_Change: (Value: string) => void;
  AutoComplete?: string;
  Placeholder?: string;
  Min_Length?: number;
  Required?: boolean;
  Show_Strength?: boolean;
  Mismatch_Message?: string | null;
};

function Icon_Eye({ ClassName }: { ClassName?: string }) {
  return (
    <svg
      className={ClassName ?? "h-4 w-4"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function Icon_Eye_Off({ ClassName }: { ClassName?: string }) {
  return (
    <svg
      className={ClassName ?? "h-4 w-4"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a20.3 20.3 0 0 1 5.06-6.08" />
      <path d="M1 1l22 22" />
      <path d="M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-.88" />
      <path d="M14.12 14.12 19 19" />
      <path d="M10.73 5.08A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a20.3 20.3 0 0 1-3.17 4.25" />
    </svg>
  );
}

export function Password_Field({
  Id,
  Label,
  Value,
  On_Change,
  AutoComplete = "current-password",
  Placeholder,
  Min_Length,
  Required = true,
  Show_Strength = false,
  Mismatch_Message = null,
}: Password_Field_Props) {
  const [Visible, Visible_Set] = useState(false);
  const Strength = Password_Strength_Score(Value);

  return (
    <div>
      <label htmlFor={Id} className={Auth_Label_Class}>
        {Label}
      </label>
      <div className="relative mt-1">
        <input
          id={Id}
          type={Visible ? "text" : "password"}
          autoComplete={AutoComplete}
          required={Required}
          minLength={Min_Length}
          value={Value}
          onChange={(Event) => On_Change(Event.target.value)}
          className={`${Auth_Input_Class} mt-0 pr-10`}
          placeholder={Placeholder}
        />
        <button
          type="button"
          onClick={() => Visible_Set((Open) => !Open)}
          className="absolute top-1/2 right-2.5 -translate-y-1/2 rounded-md p-1 text-pq-fg-subtle transition hover:bg-pq-bg-elevated/5 hover:text-slate-200"
          aria-label={Visible ? "Hide password" : "Show password"}
          aria-pressed={Visible}
        >
          {Visible ? <Icon_Eye_Off /> : <Icon_Eye />}
        </button>
      </div>

      {Show_Strength && Value.length > 0 ? (
        <div className="mt-2 space-y-1.5" aria-live="polite">
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((Segment) => (
              <div
                key={Segment}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  Strength.Level >= Segment
                    ? Password_Strength_Bar_Colors[Strength.Level]
                    : "bg-pq-bg-elevated/10"
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-pq-fg-subtle">
            Strength: <span className="text-pq-fg-muted">{Strength.Label}</span>
          </p>
        </div>
      ) : null}

      {Mismatch_Message ? (
        <p className="mt-1.5 text-xs text-red-700 dark:text-red-300">{Mismatch_Message}</p>
      ) : null}
    </div>
  );
}
